import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type LinksFunction,
  type LoaderFunctionArgs,
  type ActionFunctionArgs,
  data,
  useActionData,
  useLoaderData,
} from 'react-router'

import {
  CONTACT_FORM_CONFIRMATION_CONTENT_TYPE,
  ContactForm,
  INTENT_SEND_CONTACT_REQUEST,
  sendContactRequest,
} from '#app/components/contact-form.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Markdown } from '#app/components/markdown.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { getInstance, getLocale } from '#app/middleware/i18next.ts'
import homeStyleSheetUrl from '#app/styles/home.css?url'
import { type ApplicationContent } from '#app/types/application-content.ts'
import { getApplicationContent } from '#app/utils/application-content.server.js'
import {
  getApplicationImagesArray,
  type ApplicationImage,
} from '#app/utils/application-image.server.ts'
import { executeQuery } from '#app/utils/cms.server.ts'
import { groupedByContentType } from '#app/utils/grouped-content.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { getLocalizedContent, type LocalizedContent } from '#app/utils/i18n.ts'
import { cn, invariantResponse } from '#app/utils/misc.tsx'
import { isAuthorized } from '#app/utils/site-auth.server.ts'
import { Status } from '#app/utils/status.ts'

export const links: LinksFunction = () => {
  return [{ rel: 'stylesheet', href: homeStyleSheetUrl }]
}

enum HomeContent {
  HERO = 'Landing Hero',
  HERO_TITLE = 'Landing Hero Title',
  HERO_DESCRIPTION = 'Landing Hero Description',
  HERO_CTA = 'Landing Hero CTA',
  HERO_CTA_DESCRIPTION = 'Landing Hero CTA Description',
  ABOUT_TITLE = 'Landing About Title',
  ABOUT_DESCRIPTION = 'Landing About Description',
  ABOUT_CARD_1 = 'Landing About Card 1',
  ABOUT_CARD_2 = 'Landing About Card 2',
  ABOUT_CARD_3 = 'Landing About Card 3',
  ABOUT_DETAILS_1 = 'Landing About Details 1',
  ABOUT_DETAILS_2 = 'Landing About Details 2',
  ABOUT_BULLET_1 = 'Landing About Bullet 1',
  ABOUT_BULLET_2 = 'Landing About Bullet 2',
  ABOUT_BULLET_3 = 'Landing About Bullet 3',
  PROGRAMS_TITLE = 'Landing Programs Title',
  PROGRAMS_DESCRIPTION = 'Landing Programs Description',
  PROGRAMS_PHOTO_1 = 'Landing Programs Photo 1',
  PROGRAMS_PHOTO_2 = 'Landing Programs Photo 2',
  PROGRAMS_PHOTO_3 = 'Landing Programs Photo 3',
  CONTACT_FORM_HEADING = 'Landing Contact Form Heading',
  CONTACT_FORM_BUTTON = 'Landing Contact Form Button',
}

export async function loader({ context, request }: LoaderFunctionArgs) {
  invariantResponse(isAuthorized(request), 'Unauthorized', {
    status: 401,
  })

  const locale = getLocale(context)
  const i18next = getInstance(context)
  const t = await i18next.getFixedT(locale)

  const errorData = {
    title: t('errors.general.title'),
    description: t('errors.general.description'),
  }

  const contentQuery = `*[_type == 'applicationContent' && contentType match 'Landing*']`
  const contentResult = await executeQuery(contentQuery)

  if (contentResult.status !== Status.Success) {
    throw data(errorData, { status: 500 })
  }

  const contentData = contentResult.data as ApplicationContent[]

  const imagesResult = await getApplicationImagesArray('Landing', locale)

  if (imagesResult.status !== Status.Success) {
    throw data(errorData, { status: 500 })
  }

  const imagesData = imagesResult.images as ApplicationImage[]

  const contactFormConfirmationResult = await getApplicationContent(
    CONTACT_FORM_CONFIRMATION_CONTENT_TYPE,
  )

  if (
    contactFormConfirmationResult.status !== Status.Success ||
    !contactFormConfirmationResult.content
  ) {
    throw data(errorData, { status: 500 })
  }

  const contactFormConfirmation = contactFormConfirmationResult.content

  return {
    textContent: groupedByContentType(contentData),
    imageContent: groupedByContentType(imagesData),
    contactFormConfirmation:
      contactFormConfirmation.content as LocalizedContent,
  }
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()
  await checkHoneypot(formData)
  const intent = formData.get('intent')

  switch (intent) {
    case INTENT_SEND_CONTACT_REQUEST: {
      const request = await sendContactRequest(formData)

      if (request.status !== Status.Success) {
        return { status: Status.Error }
      }

      return { success: true, contactFormSubmitted: true }
    }
    default: {
      throw new Response(`${intent} intent is not supported`, {
        status: 400,
      })
    }
  }
}

function HeroCta({
  content,
  className,
  contactFormRef,
}: {
  content: string | undefined
  className?: string
  contactFormRef: React.RefObject<HTMLDivElement | null>
}) {
  const onClick = () => {
    contactFormRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <Button
      className={cn('text-1 lg:text-2 w-fit font-sans', className)}
      onClick={onClick}
    >
      {content}
    </Button>
  )
}

type ContentProps = {
  image:
    | {
        url: string
        alt: string
      }
    | undefined
  text: string | undefined
}

function Card({ image, text }: ContentProps) {
  return (
    <div className="rounded-6 shadow-1 lg:shadow-2 flex w-[300px] items-center justify-between bg-white p-4 pb-3 lg:h-[273px] lg:w-64 lg:flex-col lg:items-center lg:px-3 lg:pt-[28px] lg:pb-[24px] lg:text-center">
      {image && (
        <div className="box-content flex w-[80px] flex-none items-center lg:h-[100px] lg:w-auto">
          <img src={image.url} alt={image.alt} />
        </div>
      )}
      <p className="text-1 text-mauve-11 lg:text-3 pl-4 lg:pt-3 lg:pl-0">
        {text}
      </p>
    </div>
  )
}

function Bullet({ image, text }: ContentProps) {
  return (
    <div className="flex flex-row items-start gap-4 lg:gap-5">
      {image && (
        <img src={image.url} className="w-5 lg:w-7 lg:pt-1" alt={image.alt} />
      )}
      <div className="text-2 lg:text-3 lg:pl-1">
        <Markdown text={text} />
      </div>
    </div>
  )
}

function ProgramImage({
  url,
  alt,
}: {
  url: string | undefined
  alt: string | undefined
}) {
  if (!url || !alt) return null

  return (
    <img className="rounded-5 w-[100px] lg:w-[358px]" src={url} alt={alt} />
  )
}

export default function Home() {
  const { i18n } = useTranslation()
  const locale = i18n.language
  const contactFormRef = useRef<HTMLDivElement>(null)

  const { textContent, imageContent, contactFormConfirmation } =
    useLoaderData<typeof loader>()

  const actionData = useActionData<typeof action>()

  const heroCtaContent = getLocalizedContent(
    textContent[HomeContent.HERO_CTA]?.content,
    locale,
  )

  return (
    <main id="main-content" className="font-poppins flex flex-col items-center">
      <div className="relative overflow-x-hidden lg:overflow-x-visible">
        <div className="bg-home-hero absolute top-[12px] right-[-112px] h-[248px] w-[248px] lg:top-[-380px] lg:right-0 lg:h-[740px] lg:w-[740px]" />
        <section className="relative container flex w-full flex-col justify-between lg:flex-row">
          <div className="relative flex flex-col gap-2 lg:max-w-[418px] lg:gap-4 lg:pt-[101px]">
            <h1 className="text-4 text-cyan-12 lg:text-7 font-bold lg:max-w-96">
              {getLocalizedContent(
                textContent[HomeContent.HERO_TITLE]?.content,
                locale,
              )}
            </h1>
            <h2 className="text-3 text-mauve-11 lg:text-4 font-light">
              {getLocalizedContent(
                textContent[HomeContent.HERO_DESCRIPTION]?.content,
                locale,
              )}
            </h2>
            <p className="text-1 text-mauve-11 font-sans">
              {getLocalizedContent(
                textContent[HomeContent.HERO_CTA_DESCRIPTION]?.content,
                locale,
              )}
            </p>
            <HeroCta
              content={heroCtaContent}
              className="hidden lg:inline"
              contactFormRef={contactFormRef}
            />
          </div>

          <div className="relative px-6 pt-6 lg:px-0 lg:pt-[59px] lg:pl-8">
            <div className="absolute top-[16px] left-[-4px] w-[30px] lg:top-[20px] lg:left-[-48px] lg:w-[74px]">
              <img src="/img/decorative/home-hero-shapes-1.svg" alt="" />
            </div>
            <div className="absolute top-[24px] right-[8px] w-[43px] lg:top-[30px] lg:right-[-21px] lg:w-[92px]">
              <img src="/img/decorative/home-hero-shapes-2.svg" alt="" />
            </div>
            <div className="absolute right-[-10px] bottom-[-14px] w-[60px] lg:hidden">
              <img src="/img/decorative/home-hero-shapes-3.svg" alt="" />
            </div>

            <img
              className="box-content w-full lg:min-w-[644px]"
              src={imageContent[HomeContent.HERO]?.url}
              alt={imageContent[HomeContent.HERO]?.alt}
            />
          </div>

          <div className="relative flex justify-center pt-5 lg:hidden">
            <HeroCta content={heroCtaContent} contactFormRef={contactFormRef} />
          </div>
        </section>
      </div>

      <section className="relative container mt-7 flex w-full flex-col items-center lg:mt-[135px]">
        <div className="absolute top-[-213px] right-0 hidden w-[136px] lg:block">
          <img src="/img/decorative/home-hero-shapes-3.svg" alt="" />
        </div>
        <div className="absolute top-[-28px] left-[60px] w-[48px] lg:top-[-17px] lg:left-[145px] lg:w-[127px]">
          <img src="/img/decorative/home-about-shapes-1.svg" alt="" />
        </div>

        <div className="relative text-center">
          <h2 className="text-3 text-cyan-12 lg:text-6 font-bold">
            {getLocalizedContent(
              textContent[HomeContent.ABOUT_TITLE]?.content,
              locale,
            )}
          </h2>
          <p className="text-2 text-mauve-12 lg:text-3 mt-4 text-center lg:max-w-[740px]">
            {getLocalizedContent(
              textContent[HomeContent.ABOUT_DESCRIPTION]?.content,
              locale,
            )}
          </p>
        </div>

        <div className="mt-4 flex flex-col gap-4 lg:mt-6 lg:flex-row lg:gap-5">
          <Card
            image={imageContent[HomeContent.ABOUT_CARD_1]}
            text={getLocalizedContent(
              textContent[HomeContent.ABOUT_CARD_1]?.content,
              locale,
            )}
          />
          <Card
            image={imageContent[HomeContent.ABOUT_CARD_2]}
            text={getLocalizedContent(
              textContent[HomeContent.ABOUT_CARD_2]?.content,
              locale,
            )}
          />
          <Card
            image={imageContent[HomeContent.ABOUT_CARD_3]}
            text={getLocalizedContent(
              textContent[HomeContent.ABOUT_CARD_3]?.content,
              locale,
            )}
          />
        </div>
      </section>

      <div className="relative w-full">
        <div className="bg-home-about absolute bottom-[-200px] left-[-400px] hidden h-[908px] w-[908px] lg:block" />

        <div className="absolute bottom-0 left-0 hidden lg:block">
          <img src="/img/decorative/home-about-shapes-2.svg" alt="" />
        </div>

        <div className="absolute right-0 bottom-[30px] hidden lg:block">
          <img src="/img/decorative/home-about-shapes-3.svg" alt="" />
        </div>

        <section className="relative z-10 container mt-6 flex flex-col lg:mt-[72px] lg:grid lg:max-w-[1040px] lg:grid-cols-2 lg:gap-7">
          <div>
            <p className="text-3 text-purple-6 lg:text-5 text-center font-bold lg:text-left">
              {getLocalizedContent(
                textContent[HomeContent.ABOUT_DETAILS_1]?.content,
                locale,
              )}
            </p>
            <p className="text-2 text-mauve-11 lg:text-4 pt-3 text-center lg:pt-4 lg:text-left">
              {getLocalizedContent(
                textContent[HomeContent.ABOUT_DETAILS_2]?.content,
                locale,
              )}
            </p>
          </div>

          <div className="mt-5 flex flex-1 flex-col gap-4 lg:mt-0 lg:gap-5">
            <Bullet
              image={imageContent[HomeContent.ABOUT_BULLET_1]}
              text={getLocalizedContent(
                textContent[HomeContent.ABOUT_BULLET_1]?.content,
                locale,
              )}
            />
            <Bullet
              image={imageContent[HomeContent.ABOUT_BULLET_2]}
              text={getLocalizedContent(
                textContent[HomeContent.ABOUT_BULLET_2]?.content,
                locale,
              )}
            />
            <Bullet
              image={imageContent[HomeContent.ABOUT_BULLET_3]}
              text={getLocalizedContent(
                textContent[HomeContent.ABOUT_BULLET_3]?.content,
                locale,
              )}
            />
          </div>
        </section>
      </div>

      <section className="container mt-6 flex w-full flex-col items-center lg:mt-[38px]">
        <h2 className="text-3 text-cyan-12 lg:text-6 font-bold">
          {getLocalizedContent(
            textContent[HomeContent.PROGRAMS_TITLE]?.content,
            locale,
          )}
        </h2>
        <div className="text-2 text-mauve-12 lg:text-3 mt-4 text-center lg:max-w-[739px]">
          <Markdown
            text={getLocalizedContent(
              textContent[HomeContent.PROGRAMS_DESCRIPTION]?.content,
              locale,
            )}
          />
        </div>

        <div className="mt-5 flex items-center justify-center gap-4 lg:mt-6 lg:gap-5">
          <ProgramImage
            url={imageContent[HomeContent.PROGRAMS_PHOTO_1]?.url}
            alt={imageContent[HomeContent.PROGRAMS_PHOTO_1]?.alt}
          />
          <ProgramImage
            url={imageContent[HomeContent.PROGRAMS_PHOTO_2]?.url}
            alt={imageContent[HomeContent.PROGRAMS_PHOTO_2]?.alt}
          />
          <ProgramImage
            url={imageContent[HomeContent.PROGRAMS_PHOTO_3]?.url}
            alt={imageContent[HomeContent.PROGRAMS_PHOTO_3]?.alt}
          />
        </div>
      </section>

      <section className="w-full lg:container" ref={contactFormRef}>
        <ContactForm
          headingTextContent={
            textContent[HomeContent.CONTACT_FORM_HEADING]?.content
          }
          submitButtonTextContent={
            textContent[HomeContent.CONTACT_FORM_BUTTON]?.content
          }
          confirmationTextContent={contactFormConfirmation}
          showSubmittedConfirmation={actionData?.contactFormSubmitted}
        />
      </section>
    </main>
  )
}

export function ErrorBoundary() {
  return (
    <GeneralErrorBoundary
      statusHandlers={{
        500: ({ error }) => (
          <div className="text-center">
            <h1>{error.data.title}</h1>
            <p className="text-3 mt-2 font-normal">{error.data.description}</p>
          </div>
        ),
      }}
    />
  )
}
